<template>
  <div class="tasks">
    <TasksHeader
      :task-partners="partnerFilterOptions"
      :selected-partners="{ id: getSelectedPartnersFilter }"
      :task-periods="periodFilterOptions"
      :selected-periods="{ id: selectedPeriod }"
    />
    <CalendarDateSkeleton :is-loading="!isTasksDataReady" :grid-fixed-height="gridFixedHeight">
      <div
        class="calendar-wrapper"
        :style="`padding: ${calendarGaps}px 0`"
      >
        <CalendarDate
          v-if="getCalendarMode === 'days'"
          class="calendar-day"
          :disable="getSelectedPeriodFilterStartDate === null && getSelectedPeriodFilterEndDate === null"
          :value="getSelectedPeriodFilterStartDate"
          :events="getScheduledDates"
          :grid-fixed-height="gridFixedHeight"
          @date-selected="(date) => setSelectedFilter('period', { start: date, end: date }, false)"
        />
        <CalendarWeek
          v-else-if="getCalendarMode === 'weeks'"
          class="calendar-week"
          :value="getSelectedPeriodFilterStartDate"
          :grid-fixed-height="gridFixedHeight"
          @week-selected="({startDate, endDate}) => setSelectedFilter('period', { start: startDate, end: endDate }, false)"
        />
        <CalendarMonth
          v-else-if="getCalendarMode === 'months'"
          class="calendar-month"
          :value="getSelectedPeriodFilterStartDate"
          :grid-fixed-height="gridFixedHeight"
          @month-selected="({startDate, endDate}) => setSelectedFilter('period', { start: startDate, end: endDate }, false)"
        />
      </div>
    </CalendarDateSkeleton>
    <Divider type="horizontal" />
    <TasksListHeader @task-create-new="handleShowTaskPopup" />
    <TasksListSkeleton :is-loading="!isTasksDataReady" :items-count="4">
      <TasksList
        v-if="!isEmpty"
        :current-page="getCurrentPage"
        :page-size="getPerPageCount"
        :total-tasks="getTotalTasksCount"
        :tasks="getTasksList"
        @page-changed="changePaginatedPage"
        @task-clicked="handleShowInfoPopup"
      />
      <EmptyBlock v-else :title="getEmptyMessage.title" :description="getEmptyMessage.description" height="350px" />
    </TasksListSkeleton>
    <div class="archive-link flex justify-content-end">
      <SmartLink :method="() => $emit('change-view-mode','archived-tasks')" class="tasks-archive">Архів завдань</SmartLink>
    </div>

    <TaskPopup
      ref="taskPopup"
      @visibility-change="handleTaskPopupVisibilityChange"
      @task-create-new="handleCreateNewTask"
      @task-update="handleTaskUpdate"
      @file-rejected="handleFileRejected"
      @upload-error="handleUploadError"
    />
    <TaskInfoPopup
      ref="taskInfoPopup"
      @task-mark-as-finished="handleMarkTaskFinished"
      @task-mark-as-active="handleMarkTaskActive"
      @task-edit="handleEditTask"
      @task-delete="handleDeleteTaskShowConfirm"
    />
    <ConfirmationPopup
      ref="taskDeleteConfirmPopup"
      type="warning"
      title="Видалити завдання"
      message="Ви впевнені, що хочете видалити завдання?"
      @visibility-change="handleTaskDeleteConfirmPopupVisibilityChange"
      :controls="[
        {
          type: 'default',
          label: 'Видалити',
          loading: taskDeleteConfirmPopupProcessing,
          handler: this.handleDeleteTaskConfirm,
        },
        {
          type: 'regular',
          label: 'Скасувати',
          handler: this.handleTaskDeleteCancel,
        },
      ]"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import TaskInfoPopup from '../task-info-popup';
import TasksList from '../tasks-list';
import TasksListSkeleton from '../tasks-list-skeleton';
import TaskPopup from './task-popup';
import TasksHeader from './tasks-header';
import TasksListHeader from './tasks-list-header';
import { tasksEmptyMessages } from '@/features/tasks';
import { tasksPartnerFilterOptions, tasksPeriodFilterOptions } from '@/features/tasks';
import { TASKS_PARTNER_FILTER_EVENTS, TASKS_PERIOD_FILTER_EVENTS } from '@/features/tasks';
import { tasksPartnerFilter, tasksPartnerSelfFilter } from '@/features/tasks/lib';
import { filterCalculator } from '@/shared/lib/filter';
import { CalendarDate, CalendarDateSkeleton, CalendarMonth, CalendarWeek } from '@/shared/ui/calendar';
import { Divider } from '@/shared/ui/divider';
import { EmptyBlock } from '@/shared/ui/empty-block';
import { SmartLink } from '@/shared/ui/link';
import { ConfirmationPopup, POPUP_EVENTS } from '@/shared/ui/popup';

export default {
  props: {
    gridFixedHeight: {
      type: Boolean,
      default: false,
    },
    calendarGaps: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      taskPopupMode: 'create',
      taskDeleteConfirmPopupProcessing: false,
      selectedPeriod: 'today',
      partnerFilterOptions: tasksPartnerFilterOptions,
      periodFilterOptions: tasksPeriodFilterOptions,
    };
  },
  created() {
    this.$eventBus.$on(TASKS_PARTNER_FILTER_EVENTS.TASKS_PARTNER_FILTER_ALL, () => this.setSelectedFilter('partner', null, false));

    this.$eventBus.$on(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_ALL, () => this.changePeriod('all'));
    this.$eventBus.$on(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_TODAY, () => this.changePeriod('today'));
    this.$eventBus.$on(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_TOMORROW, () => this.changePeriod('tomorrow'));
    this.$eventBus.$on(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_WEEK, () => this.changePeriod('week'));
    this.$eventBus.$on(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_MONTH, () => this.changePeriod('month'));
  },
  beforeDestroy() {
    this.$eventBus.$off(TASKS_PARTNER_FILTER_EVENTS.TASKS_PARTNER_FILTER_ALL, () => this.setSelectedFilter('partner', null, false));

    this.$eventBus.$off(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_ALL, () => this.changePeriod('all'));
    this.$eventBus.$off(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_TODAY, () => this.changePeriod('today'));
    this.$eventBus.$off(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_TOMORROW, () => this.changePeriod('tomorrow'));
    this.$eventBus.$off(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_WEEK, () => this.changePeriod('week'));
    this.$eventBus.$off(TASKS_PERIOD_FILTER_EVENTS.TASKS_PERIOD_FILTER_MONTH, () => this.changePeriod('month'));
  },
  mixins: [ filterCalculator, tasksPartnerSelfFilter, tasksPartnerFilter ],
  components: { TasksHeader, CalendarDate, Divider, TasksList, SmartLink, TasksListHeader, CalendarDateSkeleton,
    TasksListSkeleton, EmptyBlock, CalendarWeek, CalendarMonth, TaskInfoPopup, TaskPopup, ConfirmationPopup },
  computed: {
    ...mapGetters('activeTasksModule', [
      'getTasksList', 'isTasksDataReady', 'isEmpty', 'getScheduledDates', 'getActiveFilters', 'getCurrentPage',
      'getTotalTasksCount', 'getPerPageCount',
    ]),
    getEmptyMessage() { return tasksEmptyMessages[0]; },
    getSelectedPartnersFilter() { return this.getActiveFilters?.partner || null; },
    getSelectedPeriodFilterStartDate() { return this.getActiveFilters?.period?.start || null; },
    getSelectedPeriodFilterEndDate() { return this.getActiveFilters?.period?.end || null; },
    getCalendarMode() {
      const calendarMode = {
        all: 'days',
        today: 'days',
        tomorrow: 'days',
        week: 'weeks',
        month: 'months',
      };

      return calendarMode[this.selectedPeriod];
    },
  },
  methods: {
    ...mapActions('activeTasksModule', [
      'fetchTasksList', 'setupFilter', 'changePaginatedPage', 'updateTaskStatus', 'deleteTask', 'createNewTask',
      'updateTask', 'deleteTaskAttachment',
    ]),
    ...mapActions('notificationModule', ['showNotificationErrorMessage']),
    setSelectedFilter(type, value, multiselect = false, refetchData = true) {
      this.setupFilter({
        filters: this.calculateFilterValues(this.getActiveFilters, type, value, multiselect),
        mode: 'update',
        refetchData
      });
      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
    changePeriod(period) {
      const periods = {
        all: {
          start: null,
          end: null,
        },
        today: {
          start: moment().startOf('day').toDate(),
          end: moment().endOf('day').toDate(),
        },
        tomorrow: {
          start: moment().startOf('day').add(1, 'day').toDate(),
          end: moment().endOf('day').add(1, 'day').toDate(),
        },
        week: {
          start: moment().startOf('week').toDate(),
          end: moment().endOf('week').toDate(),
        },
        month: {
          start: moment().startOf('month').toDate(),
          end: moment().endOf('month').toDate(),
        },
      };

      this.selectedPeriod = period;
      this.setSelectedFilter('period', periods[period], false);
    },
    handleShowInfoPopup(task) {
      this.$refs.taskInfoPopup.open(task);
    },
    handleDeleteTaskShowConfirm() {
      this.taskDeleteConfirmPopupProcessing = false;
      this.$refs.taskInfoPopup.close();
      this.$refs.taskDeleteConfirmPopup.open();
    },
    handleTaskDeleteConfirmPopupVisibilityChange({ isVisible }) {
      if (!isVisible && !this.taskDeleteConfirmPopupProcessing) {
        this.$refs.taskInfoPopup.open();
      }
    },
    async handleDeleteTaskConfirm() {
      this.taskDeleteConfirmPopupProcessing = true;
      await this.deleteTask(this.$refs.taskInfoPopup.getTask.id);
      this.$refs.taskDeleteConfirmPopup.close();
    },
    handleTaskDeleteCancel() {
      this.$refs.taskDeleteConfirmPopup.close();
    },
    async handleMarkTaskFinished() {
      this.$refs.taskInfoPopup.setProcessing(true);
      await this.updateTaskStatus({ taskId: this.$refs.taskInfoPopup.getTask.id, status: 'finished' });
      this.$refs.taskInfoPopup.close();
    },
    async handleMarkTaskActive() {
      this.$refs.taskInfoPopup.setProcessing(true);
      await this.updateTaskStatus({ taskId: this.$refs.taskInfoPopup.getTask.id, status: 'new' });
      this.$refs.taskInfoPopup.close();
    },
    handleShowTaskPopup() {
      this.$refs.taskPopup.open('create');
    },
    async handleCreateNewTask(task) {
      this.$refs.taskPopup.setProcessing(true);

      const createdTask = await this.createNewTask(task);
      await this.$refs.taskPopup.uploadFilesToServer(createdTask.id);

      this.fetchTasksList();
      this.$refs.taskPopup.close();
    },
    handleEditTask() {
      this.$refs.taskInfoPopup.close();
      const task = this.$refs.taskInfoPopup.getTask;
      this.$refs.taskPopup.open('edit', { ...task, finishDate: task.finish_date });
    },
    handleTaskPopupVisibilityChange({ isVisible }) {
      if (!isVisible && this.$refs.taskPopup.getPopupMode === 'edit' && !this.$refs.taskPopup.isProcessing) {
        this.$refs.taskInfoPopup.open();
      }
    },
    async handleTaskUpdate(task) {
      this.$refs.taskPopup.setProcessing(true);
      await this.updateTask(task);

      const files = this.$refs.taskPopup.getMarkedForDeleteFiles();
      if (files && files?.length) {
        await Promise.all(files.map(file => this.deleteTaskAttachment(file.public_id)));
      }

      await this.$refs.taskPopup.uploadFilesToServer(task.id);

      this.fetchTasksList();
      this.$refs.taskPopup.close();
    },
    handleFileRejected({ file, reason }) {
      console.log('file rejected', file, reason);
    },
    handleUploadError({ file, error }) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося завантажити файл завдання';

      this.showNotificationErrorMessage({
        error: error,
        title: `Не вдалося завантажити файл завдання ${file.name}`,
        message: errorMessage,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.tasks {
  border: 1px solid #E4E4E7;
  border-radius: 12px;
  padding: 24px 24px 48px;
  box-shadow: 0px 1px 2px 0px #0000000F, 0px 1px 3px 0px #0000001A;

  &-archive {
    color: #858C95;
    font-weight: 500;
    line-height: 15px;
    border-bottom: 1px solid #858C95;
    margin-top: 24px;

    &:hover {
      border-bottom: 1px solid transparent;
    }
  }
}

.archive-link {
  position: absolute;
  right: 24px;
  bottom: 24px;
}
</style>
